import React, { useMemo } from 'react';

import moment from 'moment';

import ApprovedTag from 'components/Tags/ApprovedTag';
import ExpirationTag from 'components/Tags/ExpirationTag';
import RejectedTag from 'components/Tags/RejectedTag';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { TravelType } from 'models/enums/travelType';
import { TravelRequested } from 'models/travelRequested';
import TravelTag from 'screens/components/TravelRequested/components/TravelTag';
import { expirationHours, splitDate } from 'utils/generic';
import { reservationImageByDestination } from 'utils/images';

import {
  Item,
  TravelInfo,
  InfoContainer,
  Info,
  Title,
  Icon,
  TravelCardHeader,
  Container,
  UserLabel,
  GuestIcon,
  GroupIcon,
  CarIcon,
  MoneyIcon,
  ArrowIcon,
  TravelDestination,
  AcceptButton,
  TravelImage,
  TravelStatus,
  PaidIcon,
  PayAtDestination,
  CheckCircleIcon,
  PayAtDestinationText,
} from './styles';
import useCompanyConfiguration from 'hooks/useCompanyConfiguration';
import { TravelProcessing } from 'screens/components/TravelRequested/components/CardFlight/styles';
import { DetailsButton, GoToArrow } from '../RequestCard/DetailsContent/styles';

interface Props {
  travel: TravelRequested;
  onClick: (id: string) => void;
  showExpirationTime?: boolean;
  showApprovedTag?: boolean;
  showRejectedTag?: boolean;
  testId?: string;
  onCancel: (id: string) => void;
  onChange?: (travel: TravelRequested) => void;
}

const TravelTitle = ({ travel }: { travel: TravelRequested }) => {
  const { t } = useStoreTranslation();
  //if (travel.type === TravelType.Vehicle)
  if (travel.vehiclePickupCity || travel.vehiclePickupCityCode)
    return (
      <>
        <CarIcon />
        <TravelDestination>
          {t('travelTravelCard.autoTitle', {
            city: travel.vehiclePickupCity || travel.vehiclePickupCityCode,
          })}
          {travel.vehicleDropoffCityCode !== travel.vehiclePickupCityCode && (
            <>
              <ArrowIcon />
              {travel.vehicleDropoffCity || travel.vehicleDropoffCityCode}
            </>
          )}
        </TravelDestination>
      </>
    );

  if (travel.type === TravelType.Hotel)
    return (
      <>
        <Icon src="/corporate/images/bedroom-outline-16-copy-2.svg" alt="hotel icon" />
        <TravelDestination>
          {t('travelTravelCard.hotelTitle', {
            city: travel.flightCity || travel.hotelCity,
          })}
        </TravelDestination>
      </>
    );

  if (travel.type === TravelType.Flight)
    return (
      <>
        <Icon src="/corporate/images/plane-outline-32-copy-3.svg" alt="plane icon" />
        <TravelDestination>
          {travel.travelAirStretch === 'Multi-Destination' && t('travelTravelCard.multiDestination')}
          {travel.travelAirStretch !== 'Multi-Destination' &&
            t('travelTravelCard.flightTitle', {
              city: travel.flightCity || travel.hotelCity || travel.arrivalPoint,
            })}
        </TravelDestination>
      </>
    );

  if (travel.type === TravelType.ExtraCharge)
    return (
      <>
        <MoneyIcon /> <TravelDestination>{t('travelTravelCard.extraCharge')}</TravelDestination>
      </>
    );

  return null;
};

const TravelCard = ({
  travel,
  showExpirationTime,
  showApprovedTag,
  showRejectedTag,
  onClick,
  testId,
  onCancel,
  onChange,
}: Props) => {
  const { t } = useStoreTranslation();
  const isExtraCharge = travel.type === TravelType.ExtraCharge;

  const formatDate = (date: string) => moment(splitDate(date)).format(t('expenseTravelCard.dateFormat'));

  const canExchange = useMemo(() => {
    if (travel.canChangeFlight === false) {
      return travel.canChangeFlight;
    }
    if (travel.type !== TravelType.Flight) return false;
    const TODAY = new Date();
    const dtDeparture = moment(travel.date);
    const dtReturn = moment(travel.returnDate);
    if (travel.travelAirStretch === 'Round-Trip') {
      if (dtReturn > moment(TODAY).add(24, 'hours')) {
        return true;
      }
    } else {
      if (dtDeparture > moment(TODAY).add(24, 'hours')) {
        return true;
      }
    }
    return false;
  }, [travel]);

  const companyConfiguration = useCompanyConfiguration();

  const havePayAtDestination = useMemo(() => {
    if (travel.type === TravelType.Hotel ) {
      return travel.payAtDestination;
    }
    return false;
  }, [travel]);

  return (
    <Item testId={testId}>
      <Container>
        {travel.type === TravelType.Hotel && (
          <TravelImage
            onClick={() => onClick(travel.id)}
            $image={reservationImageByDestination(travel.hotelCityCode)}
          ></TravelImage>
        )}

        {travel.type === TravelType.Vehicle && travel.vehiclePickupCityCode && (
          <TravelImage
            onClick={() => onClick(travel.id)}
            $image={reservationImageByDestination(travel.vehiclePickupCityCode)}
          ></TravelImage>
        )}
        <InfoContainer onClick={() => onClick(travel.id)}>
          <TravelCardHeader>
            <TravelInfo>
              {t('travelTravelCard.confirmation', {
                companyName: companyConfiguration?.companyName ?? '',
                value: travel.itineraryId.toString(),
              })}
              {travel.airlineConfirmationCode &&
                travel.airlineConfirmationCode?.toString() !== travel.itineraryId.toString() && (
                  <>
                    <br />
                    {t('travelTravelCard.locator', {
                      value: travel.airlineConfirmationCode?.toString(),
                    })}
                  </>
                )}
            </TravelInfo>
            {showExpirationTime && <ExpirationTag showIcon time={expirationHours(travel.expirationDate)} />}
            {showApprovedTag && <ApprovedTag />}
            {showRejectedTag && <RejectedTag />}
            <TravelStatus>
              {travel.travelStatus && <TravelTag status={travel.travelStatus} />}
              {/* <TravelTag status={TravelStatus.ExchangePending} /> */}
            </TravelStatus>
          </TravelCardHeader>
          {travel.travelStatus === 'Paid' &&             
            <TravelProcessing style={{ display: 'flex' }}>
              <div style={{ marginRight: '5px', paddingTop: '8px', paddingLeft: '3px'  }}><PaidIcon /></div>
              <div>{t('travelTravelCard.processingTravelMessage')}</div>
            </TravelProcessing>
          }
          <Title>
            <TravelTitle travel={travel} />
          </Title>
          <InfoContainer>
            {!isExtraCharge && (
              <Info>
                {formatDate(travel.date)} -{' '}
                {((travel.type === TravelType.Flight && travel.travelAirStretch === 'Round-Trip') ||
                  travel.type !== TravelType.Flight) && <>{formatDate(travel.returnDate)}</>}
              </Info>
            )}
            {isExtraCharge && travel.reason !== '' && <Info>{travel.reason}</Info>}
            <Info>
              {travel.name}
              <UserLabel>
                {travel.isGuest ? (
                  <>
                    <GuestIcon color="currentColor" height={16} />
                    {t('requestCard.invited')}
                  </>
                ) : (
                  <>
                    <GroupIcon color="currentColor" height={16} />
                    {travel.userGroupName}
                  </>
                )}
              </UserLabel>
            </Info>
            {havePayAtDestination && (
              <PayAtDestination>
                <CheckCircleIcon />
                <PayAtDestinationText>{t('hotelsCard.payAtDestination')}</PayAtDestinationText>
              </PayAtDestination>
            )}
          </InfoContainer>
        </InfoContainer>
        {(travel.travelStatus === 'Pending' || travel.travelStatus === 'PreApproved') && (
          <InfoContainer>
            <br />
            <br />
            <AcceptButton onClick={() => onCancel(travel.id)}>{t('travelTravelCard.cancel')}</AcceptButton>
          </InfoContainer>
        )}
        {travel.type === TravelType.Flight && onChange && canExchange && (
          <InfoContainer>
            <br />
            <br />
            <AcceptButton onClick={() => onChange(travel)}>{t('travelTravelCard.change')}</AcceptButton>
          </InfoContainer>
        )}
        <DetailsButton onClick={() => onClick(travel.id)}>
          {t('requestCard.details.showAllDetails')} <GoToArrow />
        </DetailsButton>
      </Container>
    </Item>
  );
};
export default TravelCard;
